import { type ReactElement } from 'react'
import type { Tenant } from './queries'

export type Project = {
  id: string
  name: string
  tenant: string
  description?: string
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
}

export enum DocumentTypeTag {
  SOP = 'SOP',
  WI = 'Work Instruction',
  PRD = 'PRD',
  BDD = 'BDD',
  PDF = 'PDF',
  OTHER = 'OTHER',
}

export enum BookmarkResourceType {
  SOP = 'SOP',
  DOCUMENT = 'DOCUMENT',
  PROJECT = 'PROJECT',
  AGENT = 'AGENT',
}

export enum CategoryTag {
  GetStarted = 'Get Started',
  Changeovers = 'Changeovers',
  CommonIssues = 'Common Issues',
  Others = 'Others',
}

// (referred in the api as SOP)
export type DocumentData = {
  id: string
  title: string
  creator: string
  documentRefs?: ReferenceDocumentInSOP[]
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'ERROR'
  projectId: string
  errorMessage?: string
  content?: DocumentContent
  hasDraft?: boolean
  draft?: DocumentContent
  published?: {
    content: DocumentContent
    publishedAt: string
    publishedBy: string // user id
  }
  currentVersion?: number
  customPropmpt?: string
  metadata?: DocumentMetadata & Record<string, unknown>
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
  rag?: {
    status: 'PENDING' | 'PROCESSING' | 'SYNCED' | 'FAILED'
    error?: string
    lastSyncAt?: string
  } | null
  folder?: string
}

export type DocumentVersionHistory = {
  version: number
  createdAt: string
  createdBy: string
  note?: string | null
  availableLocales: string[]
}

export type BookmarkedDocument = {
  id: string
  resourceId: string
  createdAt: string
  sop: DocumentData
}

export type DocumentContent = {
  sections?: DocumentContentSection[]
  steps?: DocumentStep[]
}

export type IllustratedItem = {
  text: string
  image?: ReferenceDocument['id']
}

export type DocumentContentSection = {
  id: string
  title: string
  order: number
  body: string
  subText?: string[]
  illustration?: ReferenceDocument['id']
  illustratedItems?: IllustratedItem[]
}

export type DocumentStepVideo = {
  id: string
  type: 'video'
  order: number
  title: string
  description: string
  additional?: {
    [key: string]: string | string[]
  } | null
  source: ReferenceDocument['id']
  keyFrame: number
  start: number
  end: number
  customThumbnail?: ReferenceDocument['id']
}

export type DocumentStepText = {
  id: string
  type: 'text'
  order: number
  title: string
  description: string
  additional?: {
    [key: string]: string | string[]
  } | null
  page?: number
  section?: string
  source?: ReferenceDocument['id']
}

export type DocumentStep = DocumentStepVideo | DocumentStepText

export type ReferenceDocumentInSOP = {
  id: string
  displayName: string
  mimeType: string
  documentType: string
}

// {
//   "filename": "8. Functional Location BOM.mp4",
//   "mimeType": "video/mp4",
//   "size": 55534291,
//   "transcode": {
//     "status": "FAILED",
//     "error": "Failed to generate output for elementary stream video-stream0. Media frames are missing starting at time 120s and ending at time 240s."
//   }
// }
// Video metadata types
export type VideoStreamDisposition = {
  default: number
  dub: number
  original: number
  comment: number
  lyrics: number
  karaoke: number
  forced: number
  hearing_impaired: number
  visual_impaired: number
  clean_effects: number
  attached_pic: number
  timed_thumbnails: number
  non_diegetic: number
  captions: number
  descriptions: number
  metadata: number
  dependent: number
  still_image: number
}

export type VideoStreamTags = {
  language?: string
  vendor_id?: string
  handler_name?: string
  creation_time?: string
  [key: string]: unknown
}

export type VideoStream = {
  index: number
  codec_name: string
  codec_long_name?: string
  profile?: string
  codec_type: string
  codec_tag_string?: string
  codec_tag?: string
  width?: number
  height?: number
  coded_width?: number
  coded_height?: number
  closed_captions?: number
  film_grain?: number
  has_b_frames?: number
  sample_aspect_ratio?: string
  display_aspect_ratio?: string
  pix_fmt?: string
  level?: number
  chroma_location?: string
  field_order?: string
  refs?: number
  is_avc?: string
  nal_length_size?: string
  r_frame_rate?: string
  avg_frame_rate?: string
  bits_per_raw_sample?: string
  extradata_size?: number
  sample_fmt?: string
  sample_rate?: string
  channels?: number
  channel_layout?: string
  bits_per_sample?: number
  initial_padding?: number
  id?: string
  time_base?: string
  start_pts?: number
  start_time?: string
  duration_ts?: number
  duration?: string
  bit_rate?: string
  disposition?: VideoStreamDisposition
  tags?: VideoStreamTags
  [key: string]: unknown
}

export type VideoMeta = {
  format?: {
    duration?: string
    size?: string
    bit_rate?: string
    [key: string]: unknown
  }
  streams?: VideoStream[]
  [key: string]: unknown
}

export type EmbeddingStatusResponse = {
  documentId: string
  embeddingStatus: 'NOT_EMBEDDED' | 'EMBEDDING' | 'EMBEDDED' | 'FAILED'
  embeddingError?: string | null
  clipCount?: number
}

// Worker-pool canonical job status — mirrors io.linewise.jobs.workerpool.JobStatus.
// Surfaces directly on DocumentResponse.jobs.<kind>.status. Distinct from the legacy
// 4-value EmbeddingStatusResponse.embeddingStatus, which is kept for backward compat.
export type JobStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'DONE'
  | 'FAILED'
  | 'NOT_APPLICABLE'
  | 'PARTIAL'
  | 'BLOCKED'

export type TranscodeJobInfo = {
  status: JobStatus
  error?: string
  blockedReason?: string
  attempts?: number
  updatedAt?: string
}

export type EmbeddingJobInfo = TranscodeJobInfo
export type MaskingJobInfo = TranscodeJobInfo
export type RagIndexJobInfo = TranscodeJobInfo

export type ImportJobInfo = TranscodeJobInfo & {
  sourceUrl?: string
}

export type DocumentJobs = {
  transcode: TranscodeJobInfo
  embedding: EmbeddingJobInfo
  masking: MaskingJobInfo
  import: ImportJobInfo
  ragIndex: RagIndexJobInfo
}

export type ReferenceDocumentMetadata = {
  filename: string
  size: number
  mimeType?: string
  videoMeta?: VideoMeta
  transcode?:
    | {
        status: 'PENDING'
      }
    | {
        status: 'PROCESSING'
      }
    | {
        status: 'RUNNING'
      }
    | {
        status: 'SUCCEEDED'
        gcsUri?: string
      }
    | {
        status: 'FAILED'
        error?: string
      }
}

// type for documents that are related to SOP (referred in the api as documents)
export type ReferenceDocument = {
  id: string
  projectId: string
  metadata: ReferenceDocumentMetadata
  source?: string
  content: string
  contentSize: number
  creator: string
  description?: string
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
  gcsUri?: string
  folder?: string
  rag?: {
    status: 'PENDING' | 'PROCESSING' | 'SYNCED' | 'FAILED'
    error?: string
    lastSyncAt?: string
  } | null
  jobs?: DocumentJobs
}

export type DocumentMetadata = {
  isBookmarked?: boolean
  type?: string
  category?: string
  views?: number
  documentNumber?: string
  version?: number
  standard?: string
  effectiveDate?: Date
  expirationDate?: Date
  processingResolution?: 'SD' | 'HD' | 'FHD' | 'Original'
}

export type Team = {
  id: string
  name: string
}

export type ResourceType = 'SOP' | 'DOCUMENT' | 'PROJECT' | 'AGENT'

// Universal search types
export type SearchResourceType =
  | 'SOP'
  | 'DOCUMENT'
  | 'PROJECT'
  | 'USER'
  | 'VIDEO_CLIP'

export type SearchResultItem = {
  resourceType: SearchResourceType
  id: string
  title: string
  subtitle?: string
  projectId?: string
  projectName?: string
  score: number
  metadata: Record<string, unknown>
}

export type SearchResponse = {
  results: Record<string, SearchResultItem[]>
  query: string
  total: number
}

export type DocumentFolder = {
  id: string
  name: string
  documents: DocumentData[]
}

export type Folder = {
  id: string
  projectId: string
  name: string
  creator: string | null
  createdAt: string
  updatedAt: string
}

export type ExpressionTreeNode = {
  name: string
  role: string | undefined
  children: {
    [key: string]: ExpressionTreeNode
  }
}
export type ExpressionTree = {
  root: ExpressionTreeNode
}

export type PermissionExpression = {
  userId: string
  expressionTree: ExpressionTree
  createdAt: string
  updatedAt: string
}

export type WorkspaceAccess = {
  inheritedRole?: string
  projectIds: Set<string>
}

/**
 * Walk the expression tree along the given path segments.
 * If any node along the path has a role, that role is inherited
 * by all descendants — return it immediately.
 * Otherwise return the children at the leaf of the path.
 */
function resolveAccessAlongPath(
  root: ExpressionTreeNode,
  path: string[],
): {
  inheritedRole?: string
  leafChildren: Record<string, ExpressionTreeNode>
} {
  let current: ExpressionTreeNode | undefined = root

  for (const segment of path) {
    if (!current) return { leafChildren: {} }
    if (current.role) return { inheritedRole: current.role, leafChildren: {} }
    current = current.children?.[segment]
  }

  if (!current) return { leafChildren: {} }
  if (current.role) return { inheritedRole: current.role, leafChildren: {} }
  return { leafChildren: current.children ?? {} }
}

/**
 * Check if the user has at least EDITOR role on any project within the tenant.
 * Returns true if the user has an inherited org-level role (ADMIN or EDITOR),
 * or if any project-level role is ADMIN or EDITOR.
 */
export function hasAnyProjectEditorAccess(
  permissionExpression: PermissionExpression | undefined,
  tenantId: string,
): boolean {
  if (!permissionExpression) return false

  const { inheritedRole, leafChildren } = resolveAccessAlongPath(
    permissionExpression.expressionTree.root,
    ['org', tenantId, 'projects'],
  )

  if (inheritedRole) {
    const upper = inheritedRole.toUpperCase()
    return upper === 'ADMIN' || upper === 'EDITOR'
  }

  return Object.values(leafChildren).some((node) => {
    if (!node?.role) return false
    const upper = node.role.toUpperCase()
    return upper === 'ADMIN' || upper === 'EDITOR'
  })
}

export type EffectiveProjectRole = 'ADMIN' | 'EDITOR' | 'VIEWER' | undefined

function normalizeRole(role: string | null | undefined): EffectiveProjectRole {
  const upper = role?.toUpperCase()
  if (upper === 'ADMIN' || upper === 'EDITOR' || upper === 'VIEWER') {
    return upper
  }
  return undefined
}

export function getEffectiveProjectRole({
  isOrgAdmin,
  isOrgEditor,
  projectRole,
}: {
  isOrgAdmin: boolean
  isOrgEditor: boolean
  projectRole: string | null | undefined
}): EffectiveProjectRole {
  const normalizedProjectRole = normalizeRole(projectRole)

  if (isOrgAdmin) return 'ADMIN'
  if (normalizedProjectRole === 'ADMIN') return 'ADMIN'
  if (isOrgEditor) return 'EDITOR'
  return normalizedProjectRole
}

export function canEditProject({
  isOrgAdmin,
  isOrgEditor,
  projectRole,
}: {
  isOrgAdmin: boolean
  isOrgEditor: boolean
  projectRole: string | null | undefined
}): boolean {
  const effectiveRole = getEffectiveProjectRole({
    isOrgAdmin,
    isOrgEditor,
    projectRole,
  })
  return effectiveRole === 'ADMIN' || effectiveRole === 'EDITOR'
}

export function canAdminProject({
  isOrgAdmin,
  isOrgEditor,
  projectRole,
}: {
  isOrgAdmin: boolean
  isOrgEditor: boolean
  projectRole: string | null | undefined
}): boolean {
  return (
    getEffectiveProjectRole({
      isOrgAdmin,
      isOrgEditor,
      projectRole,
    }) === 'ADMIN'
  )
}

export type UserInfo = {
  uid: string
  isEmailVerified: boolean
  isMfaEnabled?: boolean
  claims: {
    tenant: string
    roles: string[]
    systemRoles: string[]
  }
  issuer?: string
  name?: string
  picture?: string //uri
  email?: string
  permissionExpression?: PermissionExpression
}

export type WhoAmIResponse = {
  firebase: {
    uid: string
    email: string | null
    emailVerified: boolean
  }
  user: {
    id: string
    email: string
    tenant: string
    tenants: string[]
    tenantInfos: Tenant[]
  } | null
}

export function getUserRole(user: UserInfo): string | undefined {
  const orgNode =
    user.permissionExpression?.expressionTree?.root?.children?.org?.children
  if (!orgNode) {
    return
  }
  return Object.values(orgNode)[0]?.role
}

export function getProjectRoles(
  permissionExpression: PermissionExpression | undefined,
  tenantId: string,
): Map<string, string> {
  const map = new Map<string, string>()
  if (!permissionExpression) return map

  const orgNode =
    permissionExpression.expressionTree.root.children.org?.children
  if (!orgNode || !orgNode[tenantId]) return map

  const tenantNode = orgNode[tenantId]
  const projectsNode = tenantNode.children?.projects?.children
  if (!projectsNode) return map

  for (const [projectId, node] of Object.entries(projectsNode)) {
    if (node?.role) {
      map.set(projectId, node.role)
    }
  }

  return map
}

export function getWorkspaceAccess(
  permissionExpression: PermissionExpression | undefined,
  tenantId: string,
): WorkspaceAccess {
  if (!permissionExpression) {
    return { projectIds: new Set() }
  }

  const { inheritedRole, leafChildren } = resolveAccessAlongPath(
    permissionExpression.expressionTree.root,
    ['org', tenantId, 'projects'],
  )

  if (inheritedRole) {
    return { inheritedRole, projectIds: new Set() }
  }

  const projectIds = Object.keys(leafChildren).filter(
    (id) => leafChildren[id]?.role,
  )
  return { projectIds: new Set(projectIds) }
}

/** True when the user can see every workspace in the org (org role or inherited tree role). */
export function hasOrgWideWorkspaceAccess({
  isOrgAdmin,
  isOrgEditor,
  workspaceAccess,
}: {
  isOrgAdmin: boolean
  isOrgEditor: boolean
  workspaceAccess: WorkspaceAccess
}): boolean {
  return (
    isOrgAdmin || isOrgEditor || !!workspaceAccess.inheritedRole
  )
}

export function canAccessWorkspace(
  projectId: string,
  {
    isOrgAdmin,
    isOrgEditor,
    workspaceAccess,
  }: {
    isOrgAdmin: boolean
    isOrgEditor: boolean
    workspaceAccess: WorkspaceAccess
  },
): boolean {
  if (hasOrgWideWorkspaceAccess({ isOrgAdmin, isOrgEditor, workspaceAccess })) {
    return true
  }
  return workspaceAccess.projectIds.has(projectId)
}

export function getVisibleProjects(
  projects: Project[],
  {
    isOrgAdmin,
    isOrgEditor,
    workspaceAccess,
  }: {
    isOrgAdmin: boolean
    isOrgEditor: boolean
    workspaceAccess: WorkspaceAccess
  },
): Project[] {
  if (hasOrgWideWorkspaceAccess({ isOrgAdmin, isOrgEditor, workspaceAccess })) {
    return projects
  }
  return projects.filter((project) =>
    workspaceAccess.projectIds.has(project.id),
  )
}

export function filterDocumentsByAccess(
  documents: DocumentData[],
  access: WorkspaceAccess,
  {
    isOrgAdmin = false,
    isOrgEditor = false,
  }: { isOrgAdmin?: boolean; isOrgEditor?: boolean } = {},
): DocumentData[] {
  if (isOrgAdmin || isOrgEditor || access.inheritedRole) return documents
  return documents.filter((doc) => access.projectIds.has(doc.projectId))
}

// TODO: Edit this to match the actual user data
export type UserData = {
  id: string
  name: string
  email: string
  picture: string
}

export type VideoStorageInfo = {
  url: string
  uri: string
  expires: string
  fields: Record<string, unknown>
}

export type MimeTypeConfig = {
  icon: ReactElement
  type: string
}

export type OrgQuota = {
  name: string
  type: 'CAPACITY' | 'USAGE'
  periodType: 'LIFETIME' | 'MONTHLY'
  limit: number
  current: number
  percentage: number
  managed: boolean
  periodStart?: string
}

// Cross-tenant copy types
export interface TenantRow {
  id: string
  name: string
  tags: string[]
  parentGroupId: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface CopyDocumentRequest {
  sourceDocumentId: string
  destinationTenantId: string
  destinationProjectId: string
}

export interface CopySOPRequest {
  sourceSOPId: string
  destinationTenantId: string
  destinationProjectId: string
  copyDocuments?: boolean
}

export interface CopyDocumentResponse {
  newId: string
  message: string
}

export interface AvailableTenantEntry {
  tenant: TenantRow
  accessible: boolean
}

export type ProjectMember = {
  userId: string
  role: string
}

export type ClipCaption = {
  startSec: number
  endSec: number
  scene: string
  camera: string
  people: string
  idle: boolean
  handVisibility: number
  handPresence:
    | 'no_hands'
    | 'both_hands_full'
    | 'both_hands_almost'
    | 'partial_hands'
  objects: string[]
  actions: string[]
  safetyPresent: string[]
  safetyAbsent: string[]
}

export type Clip = {
  id: string
  startOffsetSec: number
  endOffsetSec: number
  description: string | null
  caption: ClipCaption | null
}

export type ClipsResponse = {
  documentId: string
  clips: Clip[]
  totalClips: number
}
